import { Package } from "lucide-react"
import { unreachableCategory } from "@kraahl/contract"
import { parseImages } from "@/components/my-listings/listing-utils"
import { rawImageRefValue } from "@/lib/storage/public-url"
import { getCountryName, getStateName } from "@/lib/paypal-countries"

// ADR_LISTING_SHIP_FROM_ADDRESS Phase 2: assemble display string from the
// structured ship-from trio (resolved server-side: per-listing pin →
// seller main fallback). The form's in-flight `location: string` stays
// for Phase 2; Phase 3 lifts the input itself onto the trio shape.
function assembleShipFromString(
  shipFromAddress: { city: string | null; state: string | null; country: string | null } | null | undefined,
): string {
  if (!shipFromAddress) return ""
  const { city, state, country } = shipFromAddress
  const parts: string[] = []
  if (city) parts.push(city)
  if (state && country) {
    const stateName = getStateName(state, country)
    if (stateName) parts.push(stateName)
  } else if (state) {
    parts.push(state)
  }
  if (country) {
    const countryName = getCountryName(country)
    if (countryName) parts.push(countryName)
  }
  return parts.join(", ")
}

function deriveLocationString(source: any): string {
  // Prefer structured trio when present (Phase 2 fetches emit it). Fall
  // back to legacy `location` string for fetch surfaces that still carry
  // the old shape (RPC-backed routes, in-flight payloads). DB column is
  // dropped in Phase 4.
  const fromTrio = assembleShipFromString(source?.shipFromAddress)
  if (fromTrio) return fromTrio
  return String(source?.location || "")
}
import type {
  PhotoItem,
  ShippingOption,
} from "@/components/my-listings/edit-listing-dialog"
import type {
  ListingCategory,
  CardListingDraft,
  ComicDraft,
  ListingType,
  ShippingMode,
  ParcelDraft,
} from "@/types/listing"

export type EditListingFormData = {
  title: string
  description: string
  price: number
  shipping_config: any[]
  location: string
  grade: number
  condition: string
  quantity: number
  images: PhotoItem[]
  brand_id?: string
  theme_id?: string
  product_type_id?: string
  theme_custom_text: string
  brand_custom_text: string
  product_type_custom_text: string
  scheduled_publish_at: string | null
  listingCategory?: ListingCategory
  cardDraft?: CardListingDraft
  comicDraft?: ComicDraft
  listing_format?: string
  listing_type?: ListingType
  // Phase 3 (2026-06-29) dropped the legacy `accepts_offers` field — derive
  // via `isOfferEnabledFormat(listing_format)` from @kraahl/contract.
  auto_decline_below?: number | null
  auction_start_price?: number
  auction_reserve_price?: number | null
  currency: string | null
  max_orders?: number | null
  // Shippo white-label (WS3.2 — edit-flow parity). Both fields are optional
  // because legacy listings (rows pre-WS3.1) have `shipping_mode='legacy'`
  // server-side default and NULL parcel columns — the form just doesn't
  // carry them.
  shippingMode?: ShippingMode
  parcel?: ParcelDraft
}

export const EMPTY_EDIT_FORM_DATA: EditListingFormData = {
  title: "",
  description: "",
  price: 0,
  shipping_config: [],
  location: "",
  grade: 7,
  condition: "",
  quantity: 1,
  images: [],
  brand_id: undefined,
  theme_id: undefined,
  product_type_id: undefined,
  theme_custom_text: "",
  brand_custom_text: "",
  product_type_custom_text: "",
  scheduled_publish_at: null,
  listingCategory: undefined,
  cardDraft: undefined,
  comicDraft: undefined,
  listing_format: undefined,
  listing_type: undefined,
  auto_decline_below: undefined,
  auction_start_price: undefined,
  auction_reserve_price: undefined,
  currency: null,
  shippingMode: undefined,
  parcel: undefined,
}

export function normalizeEditQuantity(raw: unknown): number {
  if (raw == null) return 1
  const n = Number(raw)
  return Number.isFinite(n) ? n : 1
}

export function normalizeImageUrls(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.filter((u): u is string => typeof u === "string" && u.length > 0)
  }
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed)
        ? parsed.filter((u: unknown): u is string => typeof u === "string" && (u as string).length > 0)
        : []
    } catch {
      return []
    }
  }
  return []
}

export function buildEditListingRow(
  data: any,
  listingId: string,
  initialListing: any
): any {
  const base = data ?? initialListing ?? {}
  const fromBase = normalizeImageUrls(base.image_urls)
  const fromInitial = normalizeImageUrls(initialListing?.image_urls)
  const image_urls = fromBase.length > 0 ? fromBase : fromInitial
  const cardDraft: CardListingDraft | undefined =
    (base as any).cardDraft ?? (initialListing as any)?.cardDraft ?? undefined
  const comicDraft: ComicDraft | undefined =
    (base as any).comicDraft ?? (initialListing as any)?.comicDraft ?? undefined
  const hasCardDetails = Array.isArray((base as any).listing_card_details)
    ? (base as any).listing_card_details.length > 0
    : false
  const hasComicDetails = Array.isArray((base as any).listing_comic_details)
    ? (base as any).listing_comic_details.length > 0
    : false
  const listingCategory: ListingCategory | undefined =
    (cardDraft?.cardCategory as ListingCategory | undefined) ??
    (hasCardDetails ? "trading_card"
      : comicDraft || hasComicDetails ? "comic"
      : undefined)
  return {
    ...base,
    id: String(base.id || listingId || ""),
    created_at: String(base.created_at || new Date().toISOString()),
    quantity: normalizeEditQuantity(base.quantity),
    image_urls,
    shipping_config: base.shipping_config ?? [],
    title: String(base.title || ""),
    description: String(base.description || ""),
    price: Number(base.price ?? 0),
    currency: (base as any)?.currency ?? initialListing?.currency ?? null,
    location: deriveLocationString(base),
    grade: base.grade ?? 7,
    condition: String(base.condition || ""),
    listing_format:
      (base as any).listing_format ?? (initialListing as any)?.listing_format ?? undefined,
    auto_decline_below:
      (base as any).auto_decline_below ?? (initialListing as any)?.auto_decline_below ?? undefined,
    auction_start_price:
      (base as any).auction_start_price ?? (initialListing as any)?.auction_start_price ?? undefined,
    auction_reserve_price:
      (base as any).auction_reserve_price ?? (initialListing as any)?.auction_reserve_price ?? undefined,
    listingCategory,
    cardDraft,
    comicDraft,
  }
}

export function computeNeedsGrading(
  form: Pick<
    EditListingFormData,
    "listingCategory" | "condition" | "cardDraft" | "comicDraft"
  >,
): boolean {
  // `undefined` ≡ lego (legacy rows without a category probe).
  switch (form.listingCategory) {
    case "trading_card":
      return Boolean(form.cardDraft?.isGraded)
    case "comic":
      return Boolean(form.comicDraft?.isGraded)
    case "lego":
    case undefined:
      return String(form.condition || "").toLowerCase() !== "new"
    default:
      return unreachableCategory(form.listingCategory)
  }
}

export function buildEditFormState(row: any): {
  formData: EditListingFormData
  selectedShippingMethods: string[]
  customShippingOptions: ShippingOption[]
  needsGrading: boolean
} {
  let parsedConfig: any[] = []
  const rawConfig = row.shipping_config ?? null
  if (Array.isArray(rawConfig)) {
    parsedConfig = rawConfig
  } else if (typeof rawConfig === "string") {
    try {
      const parsed = JSON.parse(rawConfig)
      parsedConfig = Array.isArray(parsed) ? parsed : []
    } catch {
      parsedConfig = []
    }
  }
  const methods = parsedConfig
    .map((cfg: any) => String(cfg?.shipping_method_id || ""))
    .filter(Boolean)
  const customOpts: ShippingOption[] = parsedConfig.map((cfg: any) => ({
    id: String(cfg?.shipping_method_id || ""),
    name: String(cfg?.label || cfg?.shipping_method_id || ""),
    description: "",
    icon: Package,
    price: String(cfg?.price ?? "0"),
    bannedCountries: [],
    allowCombined: Boolean(cfg?.allow_combined),
    combinedFee: String(cfg?.combined_fee ?? "0"),
  }))
  const parsedImages = parseImages(row.image_urls ?? undefined)
  const photoItems: PhotoItem[] = parsedImages.map((img: any, idx: number) => ({
    file: null,
    preview: typeof img === "string" ? img : img.preview,
    photoId: typeof img === "string" ? `existing_${idx}` : img.photoId,
  }))
  const formData: EditListingFormData = {
    title: String(row.title || ""),
    description: String(row.description || ""),
    price: Number(row.price ?? 0),
    shipping_config: parsedConfig,
    location: deriveLocationString(row),
    grade: Number(row.grade ?? 7) || 7,
    condition: String(row.condition || ""),
    quantity: normalizeEditQuantity(row.quantity),
    images: photoItems,
    brand_id: row.brand_id ?? undefined,
    theme_id: row.theme_id ?? undefined,
    product_type_id: row.product_type_id ?? undefined,
    theme_custom_text: row.theme_custom_text ?? "",
    brand_custom_text: row.brand_custom_text ?? "",
    product_type_custom_text: row.product_type_custom_text ?? "",
    scheduled_publish_at: row.scheduled_publish_at ?? null,
    listingCategory: row.listingCategory ?? undefined,
    cardDraft: row.cardDraft ?? undefined,
    comicDraft: row.comicDraft ?? undefined,
    listing_format: row.listing_format ?? undefined,
    listing_type:
      row.type === "preorder" || row.type === "standard"
        ? (row.type as ListingType)
        : undefined,
    auto_decline_below:
      row.auto_decline_below != null ? Number(row.auto_decline_below) : row.auto_decline_below === null ? null : undefined,
    auction_start_price: row.auction_start_price ?? undefined,
    auction_reserve_price: row.auction_reserve_price ?? undefined,
    currency: row.currency ?? null,
    max_orders: row.max_orders != null ? Number(row.max_orders) : row.max_orders === null ? null : undefined,
    shippingMode: row.shipping_mode === "live_rate" ? "live_rate" : row.shipping_mode === "legacy" ? "legacy" : undefined,
    // Canonical-only (slice C2): grams + mm. The edit route's select aliases
    // `parcel_weight:parcel_weight_grams` (and `_mm` siblings) so `row.parcel_*`
    // already carries canonical values. The display ⇄ locale conversion lives
    // in `ShippingModeToggleAndParcelForm`.
    parcel:
      row.parcel_weight != null ||
      row.parcel_length != null ||
      row.parcel_width != null ||
      row.parcel_height != null
        ? {
            weight: row.parcel_weight != null ? Number(row.parcel_weight) : undefined,
            length: row.parcel_length != null ? Number(row.parcel_length) : undefined,
            width: row.parcel_width != null ? Number(row.parcel_width) : undefined,
            height: row.parcel_height != null ? Number(row.parcel_height) : undefined,
          }
        : undefined,
  }
  const needsGrading = computeNeedsGrading(formData)
  return {
    formData,
    selectedShippingMethods: methods,
    customShippingOptions: customOpts,
    needsGrading,
  }
}

export function snapshotEditForm(form: EditListingFormData): string {
  // Dirty-check snapshot only — `rawImageRefValue` (not `resolveStorageUrl`):
  // resolving here would make an unchanged form look changed the moment the
  // resolver's base URL differs between renders.
  const images = (form?.images ?? []).map((i) => rawImageRefValue(i?.preview) ?? "")
  const shipping = Array.isArray(form?.shipping_config) ? form.shipping_config : []
  return JSON.stringify({
    title: String(form?.title ?? ""),
    description: String(form?.description ?? ""),
    location: String(form?.location ?? ""),
    grade: Number(form?.grade ?? 7),
    condition: String(form?.condition ?? ""),
    quantity: Number(form?.quantity ?? 1),
    brand_id: form?.brand_id ?? undefined,
    theme_id: form?.theme_id ?? undefined,
    product_type_id: form?.product_type_id ?? undefined,
    theme_custom_text: String(form?.theme_custom_text ?? ""),
    brand_custom_text: String(form?.brand_custom_text ?? ""),
    product_type_custom_text: String(form?.product_type_custom_text ?? ""),
    scheduled_publish_at: form?.scheduled_publish_at ?? null,
    listingCategory: form?.listingCategory ?? null,
    cardDraft: form?.cardDraft ?? null,
    comicDraft: form?.comicDraft ?? null,
    listing_type: form?.listing_type ?? null,
    listing_format: form?.listing_format ?? null,
    auto_decline_below: form?.auto_decline_below ?? null,
    price: Number(form?.price ?? 0),
    max_orders: form?.max_orders ?? null,
    images,
    shipping_config: shipping,
    shippingMode: form?.shippingMode ?? null,
    parcel: form?.parcel ?? null,
  })
}
